import {
  Banknote,
  Building2,
  Crown,
  Flag,
  Gavel,
  Landmark,
  LucideIcon,
  MapPin,
  Scale,
  Shield,
  Sparkles,
  SquareStack,
  Users,
} from 'lucide-react';
import type {
  GovNodeKind,
  GovNodeView,
  MatchRelation,
  NodeEnding,
  OutsideVerdict,
  ReorganisationKind,
  RunKind,
  RunStatus,
  SourceKind,

  UpdateKind,
  Verification,
} from './types';

export const KIND_LABEL: Record<GovNodeKind, string> = {
  head_of_state: 'Presidency',
  head_of_government: "Prime minister's office",
  ministry: 'Ministry',
  directorate: 'Directorate',
  agency: 'Agency',
  state_enterprise: 'State company',
  legislature: 'Legislature',
  judiciary: 'Court',
  independent_authority: 'Independent institution',
  local_government: 'Region',
  security_force: 'Security force',
  diplomatic_mission: 'Mission abroad',
  other: 'Other',
};

export const KIND_ICON: Record<GovNodeKind, LucideIcon> = {
  head_of_state: Crown,
  head_of_government: Landmark,
  ministry: Building2,
  directorate: SquareStack,
  agency: Flag,
  state_enterprise: Banknote,
  legislature: Users,
  judiciary: Gavel,
  independent_authority: Scale,
  local_government: MapPin,
  security_force: Shield,
  diplomatic_mission: Flag,
  other: Sparkles,
};

export const RELATION_LABEL: Record<MatchRelation, string> = {
  same: 'Same record',
  translation: 'Same name, other language',
  former_name: 'Former name',
  acronym: 'Acronym',
  post: 'A post of this body',
  possible: 'Possible match',
};

export const OUTSIDE_LABEL: Record<OutsideVerdict, string> = {
  foreign_government: 'Another country’s body',
  not_public: 'Not a public body',
  public_unplaced: 'Public, not placed',
  obsolete: 'No longer exists',
};

export const UPDATE_LABEL: Record<UpdateKind, string> = {
  appointment: 'Appointment',
  departure: 'Departure',
  correction: 'Correction',
  rename: 'Renamed',
  restructure: 'Reorganised',
  duplicate: 'Duplicate',
  move: 'Moved',
  former: 'Former holder',
};

export const REORG_LABEL: Record<ReorganisationKind, string> = {
  split: 'Split',
  merge: 'Merged',
  replaced: 'Replaced',
  dissolved: 'Dissolved',
};

/** What each field a change touches is called on a card */
export const CHANGE_FIELD_LABEL: Record<string, string> = {
  name: 'Name',
  officialName: 'Official name',
  acronym: 'Acronym',
  startDate: 'In post since',
  endDate: 'Left',
  organization: 'Held at',
  role: 'Title',
  parent: 'Filed under',
};

/** "Split into A and B · 15 Jul 2026" — how an entity came to an end */
export const endingSentence = (ending: NodeEnding): string => {
  const names = ending.into.map((ref) => ref.label);
  const list =
    names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  const how =
    ending.how === 'split'
      ? `Split into ${list}`
      : ending.how === 'merge'
        ? `Merged into ${list}`
        : ending.how === 'replaced'
          ? `Replaced by ${list}`
          : 'Dissolved';
  return ending.on ? `${how} · ${govDate(ending.on)}` : how;
};


export const VERIFICATION_LABEL: Record<Verification, string> = {
  corroborated: 'Two independent sources',
  single_source: 'One source',
  unverified: 'No reachable source',
  conflicting: 'Sources disagree',
  refuted: 'Contradicted by a check',
};

export const VERIFICATION_SHORT: Record<Verification, string> = {
  corroborated: 'Corroborated',
  single_source: 'Single source',
  unverified: 'Unverified',
  conflicting: 'Conflicting',
  refuted: 'Refuted',
};

export const verificationTone = (
  verification: Verification,
): 'green' | 'amber' | 'neutral' | 'red' =>
  verification === 'corroborated'
    ? 'green'
    : verification === 'single_source'
      ? 'amber'
      : verification === 'refuted'
        ? 'red'
        : 'neutral';

export const RUN_LABEL: Record<RunKind, string> = {
  structure: 'Structure',
  branch: 'One branch',
  match: 'Matching',
  officials: 'Office holders',
  appointments: 'Appointments',
  verify: 'Second check',
  curate: 'Duplicates',
  timeline: 'Timeline',
};

/** What each kind of source is called, short enough for a tag */
export const SOURCE_KIND_LABEL: Record<SourceKind, string> = {
  decree: 'Decree',
  order: 'Order',
  decision: 'Decision',
  gazette: 'Official journal',
  official_site: 'Official site',
  official_document: 'Official document',
  news: 'News',
  other: 'Other',
};

/** An act or the state's own publication, rather than a report of one */
export const isOfficialKind = (kind?: SourceKind | null): boolean =>
  !!kind && !['news', 'other'].includes(kind);

export const runTone = (status: RunStatus): 'green' | 'amber' | 'red' | 'neutral' =>
  status === 'done' ? 'green' : status === 'running' ? 'amber' : status === 'failed' ? 'red' : 'neutral';

/** "12 Mar 2026", or "Mar 2026"/"2026" when that is all that is known */
export const govDate = (value?: string | null): string => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  if (!month) return year;
  const label = months[Number(month) - 1] ?? month;
  return day ? `${Number(day)} ${label} ${year}` : `${label} ${year}`;
};

/** "3 minutes ago", for run timestamps */
export const sinceLabel = (iso?: string | null): string => {
  if (!iso) return '';
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/** "4 min 20 s", how long a run took or has been going */
export const durationLabel = (from: string, to?: string | null): string => {
  const ms = (to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime();
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min ${seconds % 60} s`;
};

export const plural = (count: number, one: string, many = `${one}s`): string =>
  `${count} ${count === 1 ? one : many}`;

export const hostOf = (url?: string | null): string | null => {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

/** What applying a node would do, in one line */
export const decisionSentence = (node: GovNodeView): string => {
  const { decision, matches } = node;
  const of = (id: string | null | undefined) =>
    matches.find((match) => match.organizationId === id) ??
    (node.organization && node.organization.id === id ? node.organization : null);
  const label = (id: string | null | undefined) => of(id)?.label ?? 'a record';
  // Whichever record is kept takes the official name, so say so: otherwise
  // "kept as Ministère du Budget" reads as though the name survives too
  const renamed = (id: string | null | undefined) =>
    of(id) && of(id)!.label !== node.name ? `, renamed “${node.name}”` : '';

  switch (decision.action) {
    case 'create':
      return 'Not in the directory — a record would be created';
    case 'link':
      return node.status === 'applied'
        ? `Recorded as “${node.organization?.label ?? label(decision.survivorId)}”`
        : `Uses the record “${label(decision.survivorId)}”${renamed(decision.survivorId)}`;
    case 'merge': {
      const others = decision.mergeIds?.length ?? 0;
      const survivor = label(decision.survivorId);
      return node.status === 'applied'
        ? `${plural(others, 'duplicate')} would be folded into “${survivor}”`
        : `${plural(others + 1, 'record')} become one — “${survivor}” is kept${renamed(decision.survivorId)}`;
    }
    case 'skip':
    default:
      return 'Left out of the directory';
  }
};

/** The stat line under the page title */
export const structureSummary = (
  stats: { total: number; applied: number; toMerge: number; toCreate: number },
  country: string,
): string =>
  [
    country,
    stats.total ? `${plural(stats.total, 'entity', 'entities')} mapped` : 'nothing mapped yet',
    stats.applied ? `${stats.applied} in the directory` : null,
  ]
    .filter(Boolean)
    .join(' · ');

/** Children by parent, in the order research found them */
export function treeOf(nodes: GovNodeView[]): {
  roots: GovNodeView[];
  childrenOf: Map<string | null, GovNodeView[]>;
  byId: Map<string, GovNodeView>;
} {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const childrenOf = new Map<string | null, GovNodeView[]>();
  for (const node of nodes) {
    // A node whose parent was rejected or deleted hangs at the top rather than vanishing
    const parentId = node.parentId && byId.has(node.parentId) ? node.parentId : null;
    childrenOf.set(parentId, [...(childrenOf.get(parentId) ?? []), node]);
  }
  childrenOf.forEach((list, key) => {
    childrenOf.set(
      key,
      [...list].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
    );
  });
  return { roots: childrenOf.get(null) ?? [], childrenOf, byId };
}

/** Every descendant of a node, including itself */
export function branchIds(
  id: string,
  childrenOf: Map<string | null, GovNodeView[]>,
  depth = 0,
): string[] {
  if (depth > 20) return [id];
  return [
    id,
    ...(childrenOf.get(id) ?? []).flatMap((child) => branchIds(child.id, childrenOf, depth + 1)),
  ];
}
